import { removeMultipleKeysFromLS, safeLocalStorage } from 'ui'

import { clientDeleteCookie, deleteClientEndCookie } from './commonUtils'
import { ALL_COOKIE_LIST, UTMCOOKIE_LIST, logoutCookieToClearWithDomain } from './constant'
import { getEnvSuffix } from './getEnvSuffix'

/**
 * Standard set of localStorage keys that should be wiped whenever the user
 * transitions between authenticated and unauthenticated states (login/logout).
 */
export const AUTH_LOCAL_STORAGE_KEYS: readonly string[] = [
  'eligibilityData',
  'utmParameters',
  'isNormalFlowWithoutUtm',
]

/** Union of cookie base names we treat as "login cleanup" targets (deduped, longest first for matching). */
const LOGIN_CLEANUP_COOKIE_BASES: readonly string[] = (() => {
  const set = new Set<string>([...ALL_COOKIE_LIST, ...logoutCookieToClearWithDomain])
  return [...set].sort((a, b) => b.length - a.length)
})()

/**
 * Patterns/prefixes that should be preserved when doing a full sweep of
 * `document.cookie`. These are non-auth cookies that the rest of the app
 * still relies on after a login redirect (utm params, sfmc analytics, etc.).
 */
const DEFAULT_PURGE_PRESERVE_PATTERNS: readonly string[] = ['existingUser', 'sfmc']
const DEFAULT_PURGE_PRESERVE_PREFIXES: readonly string[] = ['utmParams']

const getClientEnvSuffix = (): string => {
  if (typeof window === 'undefined') return ''
  return getEnvSuffix(window.location.hostname)
}

const withEnvSuffix = (names: readonly string[], envSuffix: string): string[] => {
  if (!envSuffix) return [...names]
  return names.map((name) => (name.endsWith(envSuffix) ? name : `${name}${envSuffix}`))
}

/** Deduped list of domains to use when expiring domain-scoped cookies (e.g. CURRENT_ACTIVE_DOMAIN + COOKIE_SUBDOMAIN). */
export const getCleanupDomains = (
  currentDomain?: string | null,
  currentSubDomain?: string | null,
): string[] => {
  const raw = [currentDomain, currentSubDomain]
    .map((d) => (typeof d === 'string' ? d.trim() : ''))
    .filter((d) => d.length > 0)
  return [...new Set(raw)]
}

const deleteClientEndCookieForDomains = (cookieNames: string[], domains: string[]): void => {
  if (typeof document === 'undefined') return
  if (domains.length === 0) {
    deleteClientEndCookie(cookieNames as never[], null as never)
    return
  }
  for (const domain of domains) {
    deleteClientEndCookie(cookieNames as never[], domain as never)
  }
}

/** True if `cookieName` is this base or an env / subdomain variant we set for that base. */
export const cookieNameMatchesKnownLoginBase = (
  cookieName: string,
  base: string,
  envSuffix: string,
): boolean => {
  if (cookieName === base) return true
  if (envSuffix && cookieName === `${base}${envSuffix}`) return true
  if (cookieName.startsWith(`${base}_`)) return true
  return false
}

/**
 * Reads `document.cookie` and returns exact cookie names that belong to
 * `ALL_COOKIE_LIST` or `logoutCookieToClearWithDomain` (including env-suffixed
 * and `base_subdomain_env` style names). Longer base names are matched first
 * so e.g. `tempOppId_refi` wins over `tempOppId`.
 */
export const findPresentKnownLoginCookieNames = (): string[] => {
  if (typeof document === 'undefined') return []
  const envSuffix = getClientEnvSuffix()
  const existing = document.cookie
    .split(';')
    .map((c) => c.trim().split('=')[0])
    .filter((n): n is string => Boolean(n))
  const found = new Set<string>()
  for (const name of existing) {
    for (const base of LOGIN_CLEANUP_COOKIE_BASES) {
      if (cookieNameMatchesKnownLoginBase(name, base, envSuffix)) {
        found.add(name)
        break
      }
    }
  }
  return [...found]
}

const EXPIRED_COOKIE_DATE = 'Thu, 01 Jan 1970 00:00:00 GMT'

/** Expire specific cookie names on path=/ and on each configured domain (login / cookie subdomain). */
export const expireExactClientCookies = (names: string[], domains: string[]): void => {
  if (typeof document === 'undefined' || names.length === 0) return
  const uniqueNames = [...new Set(names)]
  for (const name of uniqueNames) {
    document.cookie = `${name}=; expires=${EXPIRED_COOKIE_DATE}; path=/`
    for (const domain of domains) {
      document.cookie = `${name}=; path=/; domain=${domain}; expires=${EXPIRED_COOKIE_DATE}`
    }
  }
}

/**
 * Clears any cookies currently in the browser whose names match our known
 * lists, using both path-only and domain-scoped deletion. Call from the login
 * page with loader `currentDomain` and `currentSubDomain` (COOKIE_SUBDOMAIN).
 */
export const clearPresentKnownLoginCookiesFromDocument = (
  currentDomain?: string | null,
  currentSubDomain?: string | null,
): void => {
  if (typeof document === 'undefined') return
  const domains = getCleanupDomains(currentDomain, currentSubDomain)
  const present = findPresentKnownLoginCookieNames()
  expireExactClientCookies(present, domains)
}

/**
 * Clears the `logoutCookieToClearWithDomain` list using the current env
 * suffix and domain-aware deletion for both primary and cookie subdomain.
 */
export const clearLogoutDomainAwareCookies = (
  currentDomain?: string | null,
  currentSubDomain?: string | null,
): void => {
  if (typeof document === 'undefined') return
  const envSuffix = getClientEnvSuffix()
  const suffixed = withEnvSuffix(logoutCookieToClearWithDomain, envSuffix)
  const domains = getCleanupDomains(currentDomain, currentSubDomain)
  deleteClientEndCookieForDomains(suffixed, domains)
}

/**
 * Clears the standard set of app-session cookies (ALL_COOKIE_LIST and,
 * optionally, UTMCOOKIE_LIST) using both path-only client delete and
 * domain-aware delete for each cleanup domain.
 */
export const clearAppSessionCookies = (
  currentDomain?: string | null,
  options: { includeUtm?: boolean; currentSubDomain?: string | null } = {},
): void => {
  if (typeof document === 'undefined') return
  const { includeUtm = true, currentSubDomain } = options
  const envSuffix = getClientEnvSuffix()
  const baseList = includeUtm ? [...ALL_COOKIE_LIST, ...UTMCOOKIE_LIST] : [...ALL_COOKIE_LIST]
  const suffixed = withEnvSuffix(baseList, envSuffix)
  const domains = getCleanupDomains(currentDomain, currentSubDomain)
  clientDeleteCookie(suffixed)
  deleteClientEndCookieForDomains(suffixed, domains)
}

/**
 * Removes the standard auth/session localStorage keys, plus any extra keys
 * the caller wants to wipe in the same pass.
 */
export const clearAuthLocalStorage = (extraKeys: string[] = []): void => {
  if (typeof window === 'undefined') return
  removeMultipleKeysFromLS([...AUTH_LOCAL_STORAGE_KEYS, ...extraKeys])
}

/**
 * Hard sweep of `document.cookie`: deletes every cookie whose name does not
 * match one of the preserve patterns/prefixes. Used after login/logout to
 * make sure nothing leaks across sessions.
 */
export const purgeBrowserCookies = (options?: {
  preservePatterns?: readonly string[]
  preservePrefixes?: readonly string[]
}): void => {
  if (typeof document === 'undefined') return
  const patterns = options?.preservePatterns ?? DEFAULT_PURGE_PRESERVE_PATTERNS
  const prefixes = options?.preservePrefixes ?? DEFAULT_PURGE_PRESERVE_PREFIXES
  const expires = new Date().toUTCString()
  document.cookie?.split(';')?.forEach((rawCookie) => {
    const trimmed = rawCookie?.trim()
    if (!trimmed) return
    if (patterns.some((p) => trimmed.includes(p))) return
    if (prefixes.some((p) => trimmed.startsWith(p))) return
    document.cookie = trimmed.replace(/^ +/, '').replace(/=.*/, `=;expires=${expires};path=/`)
  })
}

/**
 * One-shot cleanup used by the login route's "ensure we're on a clean
 * slate before showing the form" effect. Uses `currentSubDomain` when set
 * so domain-scoped cookies (COOKIE_SUBDOMAIN) are cleared alongside
 * CURRENT_ACTIVE_DOMAIN. Also expires any present cookie whose name matches
 * ALL_COOKIE_LIST or logoutCookieToClearWithDomain.
 */
export const clearClientStateForLogin = (params: {
  currentDomain?: string | null
  currentSubDomain?: string | null
  existUserLocalStorageValue?: { userExist?: boolean } | undefined
}): void => {
  if (typeof document === 'undefined') return
  const { currentDomain, currentSubDomain, existUserLocalStorageValue } = params

  clearLogoutDomainAwareCookies(currentDomain, currentSubDomain)

  if (existUserLocalStorageValue?.userExist !== undefined) {
    if (!existUserLocalStorageValue.userExist) {
      clientDeleteCookie(UTMCOOKIE_LIST as string[])
    }
    clientDeleteCookie(ALL_COOKIE_LIST as string[])
  }

  clearAppSessionCookies(currentDomain, { currentSubDomain })
  clearPresentKnownLoginCookiesFromDocument(currentDomain, currentSubDomain)
}

/**
 * One-shot cleanup used by the logout route. Mirrors the cookie portion
 * of the original inline cleanup in `routes/logout.tsx`.
 */
export const clearClientStateForLogout = (
  params: {
    currentDomain?: string | null
    currentSubDomain?: string | null
  } = {},
): void => {
  if (typeof document === 'undefined') return
  clearLogoutDomainAwareCookies(params.currentDomain, params.currentSubDomain)
  clearAppSessionCookies(params.currentDomain, { currentSubDomain: params.currentSubDomain })
  clearPresentKnownLoginCookiesFromDocument(params.currentDomain, params.currentSubDomain)
}

/**
 * Convenience wrapper for the localStorage keys that the login/logout
 * routes both clear: the standard auth keys plus the one-off `temp`,
 * `loggedIn`, and `oppID` entries.
 */
export const clearLoginSessionLocalStorage = (extraKeys: string[] = []): void => {
  if (typeof window === 'undefined') return
  safeLocalStorage?.removeItem('loggedIn')
  safeLocalStorage?.removeItem('oppID')
  clearAuthLocalStorage(extraKeys)
}
